import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TokenService } from '@rappider/authentication/services';
import { environment } from '@environment';
import { AIGenerationRequest, AIGenerationType } from '../utils/ai-response-types.interface';
import { MessageType } from '@rappider/rappider-components/utils';
import {  SaveGeneratedDataModelsWithAI
} from 'libs/project/src/lib/states/project-model-state/project-model.actions';
import {
  GenerateProjectModelRelationsWithAI
} from 'libs/project/src/lib/states/project-model-relation-state/project-model-relation.actions';
import { SaveGeneratedMessageTemplatesWithAI } from
  'libs/project/src/lib/states/message-template-state/message-template.actions';
import * as ProjectModelActions from 'libs/project/src/lib/states/project-model-state/project-model.actions';
import * as ProjectModelRelationActions from 'libs/project/src/lib/states/project-model-relation-state/project-model-relation.actions';
import * as MessageTemplateActions from 'libs/project/src/lib/states/message-template-state/message-template.actions';
import * as ProjectModelEndpointActions from 'libs/project/src/lib/states/project-model-endpoint-state/project-model-endpoint.actions';
import * as PageActions from 'libs/pages/src/lib/state/page-state/page.actions';
import * as ProjectThemeActions from 'libs/project/src/lib/states/project-theme-state/project-theme.actions';
import * as WorkflowActions from 'libs/diagram-editor/src/lib/state/diagram-state/diagram.actions';
import {
  OpenAiDataModelRelationResponseDto,
  OpenAiDataModelResponseDto,
  OpenAiDataModelRelationExampleRelationDto,
  ProjectChannelMessage
} from '@rappider/rappider-sdk';

/**
 * AIChatGeneratorService orchestrates AI generation requests and manages content saving operations.
 * Provides centralized AI generation orchestration for different content types and handles NgRx action dispatching.
 * Manages API interactions for component compilation and serves as the core AI generation hub.
 */
@Injectable({
  providedIn: 'root'
})
export class AIChatGeneratorService implements OnDestroy {

  private destroy$ = new Subject<void>();

  // ApproveBranch API special Subjects
  private approveBranchSuccess$ = new Subject<{ componentDefinitionId: string }>();
  private approveBranchStarted$ = new Subject<{ componentDefinitionId: string }>();
  private approveBranchError$ = new Subject<{ componentDefinitionId: string; error: unknown }>();

  // setDisplayComponent API special Subjects
  private setDisplayComponentSuccess$ = new Subject<{ projectModelId: string; componentDefinitionId: string }>();
  private setDisplayComponentError$ = new Subject<{ projectModelId: string; error: unknown }>();
  private setDisplayComponentStarted$ = new Subject<{ projectModelId: string; componentDefinitionId: string }>();

  constructor(
    private store: Store<unknown>,
    private actions$: Actions,
    private http: HttpClient,
    private tokenService: TokenService
  ) {
    // Constructor intentionally left empty
  }

  /**
   * Process AI generation request based on type and dispatch appropriate actions
   * @param request - AI generation request containing type, prompt, and project ID
   */
  processAIGenerationRequest(request: AIGenerationRequest): void {
    if (!request.type || !request.projectId) {
      return;
    }
    const actionId = Date.now().toString();

    switch (request.type) {
      case AIGenerationType.DATA_MODEL:
        this.store.dispatch(new GenerateProjectModelRelationsWithAI({
          aiPrompt: request.prompt,
          actionId: actionId
        }));
        break;

      case AIGenerationType.ENDPOINT:
        this.store.dispatch(ProjectModelEndpointActions.GenerateEndpointWithAI({
          payload: {
            aiPrompt: request.prompt,
            projectId: request.projectId,
            programmingLanguage: 'nodejs:18'
          }
        }));
        break;

      case AIGenerationType.ENTITY_COMPONENT:
        break;

      case AIGenerationType.PAGE:
        this.store.dispatch(new PageActions.GeneratePageWithAI({
          aiPrompt: request.prompt,
          projectId: request.projectId
        }));
        break;

      case AIGenerationType.PAGE_LAYOUT:
        this.store.dispatch(new PageActions.GeneratePageWithAI({
          aiPrompt: request.prompt,
          projectId: request.projectId
        }));
        break;

      case AIGenerationType.THEME:
        this.store.dispatch(new ProjectThemeActions.GenerateThemeWithAI({
          aiPrompt: request.prompt,
          projectId: request.projectId
        }));
        break;

      case AIGenerationType.MESSAGE_TEMPLATE:
        this.store.dispatch(new MessageTemplateActions.GenerateMessageTemplateWithAI({
          aiPrompt: request.prompt,
          actionId: actionId
        }));
        break;

      case AIGenerationType.WORKFLOW:
        this.store.dispatch(WorkflowActions.GenerateWorkflowWithAI({
          payload: {
            aiPrompt: request.prompt,
            projectId: request.projectId,
            programmingLanguage: 'nodejs:18',
            force: true,
            uiDataEventId: undefined
          }
        }));
        break;

      default:
        break;
    }
  }


  /**
   * Save generated content with AI based on type
   * @param generatedContent - Generated content to save
   * @param type - Type of AI generation
   * @param projectId - Project ID to save content to
   */
  saveGeneratedContentWithAI(generatedContent: unknown, type: AIGenerationType, projectId: string): void {
    if (!type || !projectId) {
      return;
    }

    switch (type) {
      case AIGenerationType.DATA_MODEL:
        this.saveDataModels(generatedContent as unknown[], projectId);
        break;

      case AIGenerationType.MESSAGE_TEMPLATE:
        this.saveMessageTemplates(generatedContent as unknown[], projectId);
        break;

      default:
        break;
    }
  }

  /**
   * Save generated data models to project
   * @param generatedDataModels - Array of generated data models
   * @param projectId - Project ID to save models to
   */
  private saveDataModels(generatedDataModels: unknown[], projectId: string): void {
    const requestData = generatedDataModels.map((model: unknown) => {
      const modelObj = model as Record<string, unknown>;
      return {
        modelName: modelObj.modelName || modelObj.name || 'Generated Model',
        description: modelObj.description || 'AI Generated Data Model',
        exampleData: modelObj.exampleData || []
      };
    });

    this.store.dispatch(new SaveGeneratedDataModelsWithAI({
      generatedDataModels: requestData,
      projectId: projectId
    }));
  }

  /**
   * Save generated data models with relations to project
   * @param responseData - Response data containing models and relations
   * @param projectId - Project ID to save models to
   */
  saveGeneratedDataModelsWithRelations(responseData: unknown, projectId: string): void {
    if (!responseData || !projectId) {
      return;
    }

    const dataObj = responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    this.saveDataModelsWithRelations(generated, projectId);
  }

  /**
   * Save data models with relations using ProjectModelRelationControllerService
   * @param responseData - Response data containing models and relations
   * @param projectId - Project ID to save models to
   */
  private saveDataModelsWithRelations(responseData: Record<string, unknown>, projectId: string): void {
    const models = responseData.models || [];
    const relations = responseData.relations || [];

    const relationData: OpenAiDataModelRelationResponseDto = {
      models: Array.isArray(models) ? models.map((model: unknown) => {
        const modelObj = model as Record<string, unknown>;
        return {
          modelName: modelObj.modelName || modelObj.name || 'Generated Model',
          description: modelObj.description || 'AI Generated Data Model',
          exampleData: modelObj.exampleData || []
        } as OpenAiDataModelResponseDto;
      }) : [],
      relations: Array.isArray(relations) ? relations.map((relation: unknown) => {
        const relationObj = relation as Record<string, unknown>;
        return {
          name: relationObj.name || 'Generated Relation',
          description: relationObj.description || '',
          sourceModel: relationObj.sourceModel || '',
          sourceField: relationObj.sourceField || '',
          targetModel: relationObj.targetModel || '',
          targetField: relationObj.targetField || '',
          throughModel: relationObj.throughModel || undefined,
          type: relationObj.type || 'hasOne'
        } as OpenAiDataModelRelationExampleRelationDto;
      }) : []
    };

    this.store.dispatch(new ProjectModelRelationActions.GenerateDataModelsWithRelations({
      relationData: relationData,
      projectId: projectId
    }));
  }

  /**
   * Save generated message templates to project
   * @param generatedMessageTemplates - Array of generated message templates
   * @param projectId - Project ID to save templates to
   */
  private saveMessageTemplates(generatedMessageTemplates: unknown[], projectId: string): void {
    this.store.dispatch(new SaveGeneratedMessageTemplatesWithAI({
      generatedMessageTemplates: generatedMessageTemplates,
      projectId: projectId
    }));
  }

  /**
   * Subscribe to AI generation success events
   * @returns Observable stream of AI generation success events
   */
  subscribeToAIGenerationSuccess(): Observable<unknown> {
    return this.actions$.pipe(
      ofType(
        ProjectModelRelationActions.ActionTypes.GenerateProjectModelRelationsWithAISuccessful,
        MessageTemplateActions.ActionTypes.GenerateMessageTemplateWithAISuccessful,
        ProjectModelEndpointActions.ActionTypes.GenerateEndpointWithAISuccessful,
        PageActions.ActionTypes.GeneratePageWithAISuccessful,
        ProjectThemeActions.ActionTypes.GenerateThemeWithAISuccessful,
        WorkflowActions.ActionTypes.GenerateWorkflowWithAISuccessful
      ),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Subscribe to AI generation error events
   * @returns Observable stream of AI generation error events
   */
  subscribeToAIGenerationError(): Observable<unknown> {
    return this.actions$.pipe(
      ofType(
        ProjectModelRelationActions.ActionTypes.GenerateProjectModelRelationsWithAIFailure,
        MessageTemplateActions.ActionTypes.GenerateMessageTemplateWithAIFailure,
        ProjectModelEndpointActions.ActionTypes.GenerateEndpointWithAIFailure,
        PageActions.ActionTypes.GeneratePageWithAIFailure,
        ProjectThemeActions.ActionTypes.GenerateThemeWithAIFailure,
        WorkflowActions.ActionTypes.GenerateWorkflowWithAIFailure
      ),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Subscribe to save success events
   * @returns Observable stream of save success events
   */
  subscribeToSaveSuccess(): Observable<unknown> {
    return this.actions$.pipe(
      ofType(
        ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAISuccessful,
        ProjectModelRelationActions.ActionTypes.GenerateDataModelsWithRelationsSuccessful,
        MessageTemplateActions.ActionTypes.SaveGeneratedMessageTemplatesWithAISuccessful
      ),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Subscribe to save error events
   * @returns Observable stream of save error events
   */
  subscribeToSaveError(): Observable<unknown> {
    return this.actions$.pipe(
      ofType(
        ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAIFailure,
        ProjectModelRelationActions.ActionTypes.GenerateDataModelsWithRelationsFailure,
        MessageTemplateActions.ActionTypes.SaveGeneratedMessageTemplatesWithAIFailure
      ),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Subscribe to approveBranch started events
   * @returns Observable stream of approveBranch started events
   */
  subscribeToApproveBranchStarted(): Observable<{ componentDefinitionId: string }> {
    return this.approveBranchStarted$.asObservable().pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Subscribe to approveBranch success events
   * @returns Observable stream of approveBranch success events
   */
  subscribeToApproveBranchSuccess(): Observable<{ componentDefinitionId: string }> {
    return this.approveBranchSuccess$.asObservable().pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Subscribe to approveBranch error events
   * @returns Observable stream of approveBranch error events
   */
  subscribeToApproveBranchError(): Observable<{ componentDefinitionId: string; error: unknown }> {
    return this.approveBranchError$.asObservable().pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Notify that approveBranch operation has started
   * @param componentDefinitionId - Unique identifier of the component definition
   */
  notifyApproveBranchStarted(componentDefinitionId: string): void {
    this.approveBranchStarted$.next({ componentDefinitionId });
  }

  /**
   * Notify that approveBranch operation has succeeded
   * @param componentDefinitionId - Unique identifier of the component definition
   */
  notifyApproveBranchSuccess(componentDefinitionId: string): void {
    this.approveBranchSuccess$.next({ componentDefinitionId });
  }

  /**
   * Notify that approveBranch operation has failed
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param error - Error that occurred
   */
  notifyApproveBranchError(componentDefinitionId: string, error: unknown): void {
    this.approveBranchError$.next({ componentDefinitionId, error });
  }

  /**
   * Subscribe to setDisplayComponent started events
   * @returns Observable stream of setDisplayComponent started events
   */
  subscribeToSetDisplayComponentStarted(): Observable<{ projectModelId: string; componentDefinitionId: string }> {
    return this.setDisplayComponentStarted$.asObservable().pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Subscribe to setDisplayComponent success events
   * @returns Observable stream of setDisplayComponent success events
   */
  subscribeToSetDisplayComponentSuccess(): Observable<{ projectModelId: string; componentDefinitionId: string }> {
    return this.setDisplayComponentSuccess$.asObservable().pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Subscribe to setDisplayComponent error events
   * @returns Observable stream of setDisplayComponent error eventss
   */
  subscribeToSetDisplayComponentError(): Observable<{ projectModelId: string; error: unknown }> {
    return this.setDisplayComponentError$.asObservable().pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Notify that setDisplayComponent operation has started
   * @param projectModelId - Unique identifier of the project model
   * @param componentDefinitionId - Unique identifier of the component definition
   */
  notifySetDisplayComponentStarted(projectModelId: string, componentDefinitionId: string): void {
    this.setDisplayComponentStarted$.next({ projectModelId, componentDefinitionId });
  }

  /**
   * Notify that setDisplayComponent operation has succeeded
   * @param projectModelId - Unique identifier of the project model
   * @param componentDefinitionId - Unique identifier of the component definition
   */
  notifySetDisplayComponentSuccess(projectModelId: string, componentDefinitionId: string): void {
    this.setDisplayComponentSuccess$.next({ projectModelId, componentDefinitionId });
  }

  /**
   * Notify that setDisplayComponent operation has failed
   * @param projectModelId - Unique identifier of the project model
   * @param error - Error that occurred
   */
  notifySetDisplayComponentError(projectModelId: string, error: unknown): void {
    this.setDisplayComponentError$.next({ projectModelId, error });
  }

  // ========================================
  // API CALLS
  // ========================================


  /**
   * Generate AI response message based on content type
   * @param responseData - AI response data
   * @param contentType - Type of content (data-model, entity-component, etc.)
   * @returns Generated message string
   */
  generateResponseMessage(responseData: unknown, contentType: string = 'content'): string {
    if (!responseData) {
      return this.getDefaultSuccessMessage(contentType);
    }

    if (typeof responseData === 'object' && responseData !== null) {
      const dataObj = responseData as Record<string, unknown>;

      // Check if models array is empty
      if (dataObj.models && Array.isArray(dataObj.models) && dataObj.models.length === 0) {
        return 'Something went wrong. Please try again.';
      }

      // Use summary if available
      if (dataObj.summary && typeof dataObj.summary === 'string') {
        return dataObj.summary;
      }
    }

    return this.getDefaultSuccessMessage(contentType);
  }

  /**
   * Get default success message based on content type
   * @param contentType - Type of content
   * @returns Default success message
   */
  private getDefaultSuccessMessage(contentType: string): string {
    switch (contentType) {
      case 'data-model':
        return 'Data models have been generated successfully';
      case 'entity-component':
        return 'Entity component has been generated successfully';
      case 'message-template':
        return 'Message templates have been generated successfully';
      case 'endpoint':
        return 'API endpoint has been generated successfully';
      case 'page':
        return 'Page has been generated successfully';
      case 'theme':
        return 'Theme has been generated successfully';
      case 'workflow':
        return 'Workflow has been generated successfully';
      default:
        return 'Content has been generated successfully';
    }
  }

  /**
   * Generate error message for failed operations
   * @returns Error message for failed operations
   */
  generateErrorMessage(): string {
    return 'Something went wrong. Please try again.';
  }

  /**
   * Create AI response message object for chat
   * @param responseData - Response data from AI generation
   * @param options - Message creation options
   * @returns ProjectChannelMessage object
   */
  createAIResponseMessage(
    responseData: unknown,
    options: {
      messageId: string;
      projectId: string;
      projectModelId?: string;
      componentDefinitionId?: string;
      responseType: AIGenerationType;
      isEmptyResponse?: boolean;
      senderId?: string;
    }
  ): ProjectChannelMessage {
    const responseDataObj = responseData as Record<string, unknown>;
    const isEmptyResponse = options.isEmptyResponse ?? !responseDataObj;
    const messageText = isEmptyResponse
      ? this.generateErrorMessage()
      : this.generateResponseMessage(responseDataObj, this.getContentTypeFromResponseType(options.responseType));

    return {
      id: options.messageId,
      message: messageText,
      date: new Date().toISOString(),
      channelId: 'ai-channel',
      projectId: options.projectId,
      projectModelId: options.projectModelId,
      componentDefinitionId: options.componentDefinitionId,
      senderId: options.senderId || '00000000-0000-0000-0000-000000000001',
      status: 'new',
      responseData: isEmptyResponse ? null : responseDataObj,
      responseType: options.responseType,
      isEmptyResponse,
      type: MessageType.AI,
      messageId: options.messageId
    } as ProjectChannelMessage;
  }

  /**
   * Get content type string from AIGenerationType enum
   * @param responseType - AI generation type
   * @returns Content type string
   */
  private getContentTypeFromResponseType(responseType: AIGenerationType): string {
    switch (responseType) {
      case AIGenerationType.DATA_MODEL:
        return 'data-model';
      case AIGenerationType.ENTITY_COMPONENT:
        return 'entity-component';
      default:
        return 'content';
    }
  }

  /**
   * Process AI response actions if available
   * @param responseData - AI response data that may contain actions
   * @returns Observable of action processing result
   */
  processAIResponseActions(responseData: unknown): Observable<unknown> {
    if (!responseData || typeof responseData !== 'object') {
      return of(null);
    }

    const dataObj = responseData as Record<string, unknown>;
    const actions = dataObj.actions || (dataObj.generated as Record<string, unknown>)?.actions;

    if (!actions || !Array.isArray(actions) || actions.length === 0) {
      return of(null);
    }

    const updateRequest = actions[0] as Record<string, unknown>;
    return this.executeActionRequest(updateRequest);
  }

  /**
   * Execute action request with HTTP method
   * @param actionRequest - Action request object containing path, method, and body
   * @returns Observable of action execution result
   */
  private executeActionRequest(actionRequest: Record<string, unknown>): Observable<unknown> {
    const path = actionRequest.path as string;
    const method = (actionRequest.method as string)?.toLowerCase() || 'post';
    const body = actionRequest.body as Record<string, unknown>;

    if (!path) {
      return of({ error: 'Action request path is missing' });
    }

    const url = `${environment.apiBaseUrl}${path}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.tokenService.getAuthenticationToken()
    });

    switch (method) {
      case 'post':
        return this.http.post(url, body, { headers }).pipe(
          catchError(() => of({ error: 'Action execution failed' }))
        );
      case 'put':
        return this.http.put(url, body, { headers }).pipe(
          catchError(() => of({ error: 'Action execution failed' }))
        );
      case 'patch':
        return this.http.patch(url, body, { headers }).pipe(
          catchError(() => of({ error: 'Action execution failed' }))
        );
      case 'delete':
        return this.http.delete(url, { headers }).pipe(
          catchError(() => of({ error: 'Action execution failed' }))
        );
      default:
        return of({ error: 'Unsupported HTTP method' });
    }
  }

  /**
   * Validate AI response data for completeness
   * @param responseData - Response data to validate
   * @returns True if response is empty or invalid
   */
  validateResponseData(responseData: unknown): boolean {
    if (!responseData) {
      return false;
    }

    if (Array.isArray(responseData)) {
      return responseData.length > 0;
    }

    if (typeof responseData === 'object') {
      const obj = responseData as Record<string, unknown>;
      const hasData = Object.keys(obj).some(key => {
        const value = obj[key];
        return value !== null && value !== undefined && value !== '';
      });
      return hasData;
    }

    return true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.approveBranchSuccess$.complete();
    this.approveBranchStarted$.complete();
    this.approveBranchError$.complete();
    this.setDisplayComponentSuccess$.complete();
    this.setDisplayComponentError$.complete();
    this.setDisplayComponentStarted$.complete();
  }
}
